import { Intersection } from "../Intersection.js";
import { ORIENTATIONS } from "../../../constants/intxConstants.js";
import { errDiv } from "../../../util/mathHelpers.ts";

// const DEBUG = true;

/** Echelon computational class. Extends the Intersection parent class */
export class Echelon extends Intersection {
  /**
   * Constructor for the Echelon class.
   * @param {string} name - Name of the intersection
   * @param {Object} volumes - Object mapping direction strings (eastbound, westbound, northbound, southbound) to  {@link Volume} objects.
   * @param {Object} globalParams - Object containing the required global parameters for an intersection.
   * @param {string} majorStDirection - String indicating the approach that shares a zone with the NB approach.
   */
  constructor(
    name,
    volumes,
    globalParams,
    majorStDirection = ORIENTATIONS.NORTH_SOUTH
  ) {
    super(name, volumes, globalParams);

    this.majorStDirection = majorStDirection;

    this.LaneConfig = {
      Major1: {
        LTLanes: 1,
        LTShared: false,
        TLanes: 1,
        RTLanes: 1,
        RTShared: false,
        RTChan: false,
      },
      Major2: {
        LTLanes: 1,
        LTShared: false,
        TLanes: 1,
        RTLanes: 1,
        RTShared: false,
        RTChan: false,
      },
      Minor1: {
        LTLanes: 1,
        LTShared: false,
        TLanes: 1,
        RTLanes: 1,
        RTShared: false,
        RTChan: false,
      },
      Minor2: {
        LTLanes: 1,
        LTShared: false,
        TLanes: 1,
        RTLanes: 1,
        RTShared: false,
        RTChan: false,
      },
    };
  }

  /**
   * Function to get the DEFAULT inputs available for the intersection.  This function is designed to facilitate the
   * integration of the engine into a user interface.
   *
   * An echelon has a single input zone (Z5) and two output zones (Z5, Z6). The lane configuration inputs are
   * dependent on Major/Minor street designation.
   *
   * @return {Object} Object representation of default inputs
   */
  static getZoneDefaultInputs() {
    return {
      Z5Z6: {
        Major1: {
          LT: 1,
          LTShared: false,
          T: 1,
          RT: 1,
          RTShared: false,
          RTChan: false,
        },
        Major2: {
          LT: 1,
          LTShared: false,
          T: 1,
          RT: 1,
          RTShared: false,
          RTChan: false,
        },
        Minor1: {
          LT: 1,
          LTShared: false,
          T: 1,
          RT: 1,
          RTShared: false,
          RTChan: false,
        },
        Minor2: {
          LT: 1,
          LTShared: false,
          T: 1,
          RT: 1,
          RTShared: false,
          RTChan: false,
        },
      },
    };
  }

  static getLaneConfigLabelsMapping(orientation) {
    const isEW = orientation === ORIENTATIONS.EAST_WEST;
    return {
      Z5Z6: this.getIntersectionLabelsMappingWithMajorDir(isEW),
    };
  }

  setLaneConfigInputs(laneConfigInputs) {
    const newLaneConfig = laneConfigInputs.Z5Z6;
    this.LaneConfig.Major1.LTLanes = newLaneConfig.Major1.LT;
    this.LaneConfig.Major1.LTShared = newLaneConfig.Major1.LTShared;
    this.LaneConfig.Major1.TLanes = newLaneConfig.Major1.T;
    this.LaneConfig.Major1.RTLanes = newLaneConfig.Major1.RT;
    this.LaneConfig.Major1.RTShared = newLaneConfig.Major1.RTShared;
    this.LaneConfig.Major1.RTChan = newLaneConfig.Major1.RTChan;

    this.LaneConfig.Major2.LTLanes = newLaneConfig.Major2.LT;
    this.LaneConfig.Major2.LTShared = newLaneConfig.Major2.LTShared;
    this.LaneConfig.Major2.TLanes = newLaneConfig.Major2.T;
    this.LaneConfig.Major2.RTLanes = newLaneConfig.Major2.RT;
    this.LaneConfig.Major2.RTShared = newLaneConfig.Major2.RTShared;
    this.LaneConfig.Major2.RTChan = newLaneConfig.Major2.RTChan;

    this.LaneConfig.Minor1.LTLanes = newLaneConfig.Minor1.LT;
    this.LaneConfig.Minor1.LTShared = newLaneConfig.Minor1.LTShared;
    this.LaneConfig.Minor1.TLanes = newLaneConfig.Minor1.T;
    this.LaneConfig.Minor1.RTLanes = newLaneConfig.Minor1.RT;
    this.LaneConfig.Minor1.RTShared = newLaneConfig.Minor1.RTShared;
    this.LaneConfig.Minor1.RTChan = newLaneConfig.Minor1.RTChan;

    this.LaneConfig.Minor2.LTLanes = newLaneConfig.Minor2.LT;
    this.LaneConfig.Minor2.LTShared = newLaneConfig.Minor2.LTShared;
    this.LaneConfig.Minor2.TLanes = newLaneConfig.Minor2.T;
    this.LaneConfig.Minor2.RTLanes = newLaneConfig.Minor2.RT;
    this.LaneConfig.Minor2.RTShared = newLaneConfig.Minor2.RTShared;
    this.LaneConfig.Minor2.RTChan = newLaneConfig.Minor2.RTChan;
  }

  // Implements the computeVCAnalysis function of the Intersection parent class.
  _runCriticalMovementAnalysis() {
    const volumes = this.generateDirectionalVolumes(this.majorStDirection);
    // Determine movment volumes for all directions
    const intx = {
      Major1: { criticalVol: 0 },
      Major2: { criticalVol: 0 },
      Minor1: { criticalVol: 0 },
      Minor2: { criticalVol: 0 },
    };

    const minorMovementList = ["Minor1", "Minor2"];
    for (let mi = 0; mi < minorMovementList.length; mi++) {
      const mvmt = minorMovementList[mi];

      intx[mvmt].RT = 0;
      intx[mvmt].T = volumes[mvmt].T;
      if (this.LaneConfig[mvmt].RTChan === false) {
        intx[mvmt].RT = volumes[mvmt].RT;
        if (this.LaneConfig[mvmt].RTShared) {
          intx[mvmt].RT = Math.round(
            intx[mvmt].RT *
              (this.LaneConfig[mvmt].RTLanes /
                (this.LaneConfig[mvmt].RTLanes + 1))
          );
        }
        // Assign any shared rights to through movement
        intx[mvmt].T += Math.round(
          (volumes[mvmt].RT - intx[mvmt].RT) / this.RTAF
        );
      }
      intx[mvmt].LT = volumes[mvmt].LT;
      let leftTurnFactor = 1;
      if (this.LaneConfig[mvmt].LTShared && this.LaneConfig[mvmt].LTLanes < 2) {
        intx[mvmt].LT = Math.round(
          intx[mvmt].LT *
            (this.LaneConfig[mvmt].LTLanes /
              (this.LaneConfig[mvmt].LTLanes + 1))
        );
        leftTurnFactor /= this.LTAF;
      }
      intx[mvmt].T += Math.round(
        (volumes[mvmt].LT - intx[mvmt].LT) * leftTurnFactor
      );

      // Determine Critical Volumes for each direction
      // MAX(IFERROR(Z5WBL / LTAF / Z5WBLlanes,0),IFERROR(Z5WBT / Z5WBTlanes,0),IFERROR(Z5WBR / RTAF / Z5WBRlanes,0) -IFERROR(Z5SBL / LTAF / Z5SBLlanes,0))
      // console.log(
      //   "MAX(minor): ",
      //   errDiv(intx[mvmt].LT, this.LTAF, this.LaneConfig[mvmt].LTLanes),
      //   errDiv(intx[mvmt].T, this.LaneConfig[mvmt].TLanes),
      //   errDiv(intx[mvmt].RT, this.RTAF, this.LaneConfig[mvmt].RTLanes)
      // );
      intx[mvmt].criticalVol = Math.round(
        Math.max(
          errDiv(intx[mvmt].LT, this.LTAF, this.LaneConfig[mvmt].LTLanes),
          errDiv(intx[mvmt].T, this.LaneConfig[mvmt].TLanes),
          errDiv(intx[mvmt].RT, this.RTAF, this.LaneConfig[mvmt].RTLanes)
        )
      );
    }

    const majorMovementList = ["Major1", "Major2"];
    for (let mi = 0; mi < majorMovementList.length; mi++) {
      const mvmt = majorMovementList[mi];
      const adjmvmt = minorMovementList[mi];

      intx[mvmt].RT = 0;
      intx[mvmt].T = volumes[mvmt].T;
      if (this.LaneConfig[mvmt].RTChan === false) {
        intx[mvmt].RT = volumes[mvmt].RT;
        if (this.LaneConfig[mvmt].RTShared) {
          intx[mvmt].RT = Math.round(
            intx[mvmt].RT *
              (this.LaneConfig[mvmt].RTLanes /
                (this.LaneConfig[mvmt].RTLanes + 1))
          );
        }
        // Assign any shared rights to through movement
        intx[mvmt].T += Math.round(
          (volumes[mvmt].RT - intx[mvmt].RT) / this.RTAF
        );
      }
      intx[mvmt].LT = volumes[mvmt].LT;
      let leftTurnFactor = 1;
      if (this.LaneConfig[mvmt].LTShared && this.LaneConfig[mvmt].LTLanes < 2) {
        intx[mvmt].LT = Math.round(
          intx[mvmt].LT *
            (this.LaneConfig[mvmt].LTLanes /
              (this.LaneConfig[mvmt].LTLanes + 1))
        );
        leftTurnFactor /= this.LTAF;
      }
      intx[mvmt].T += Math.round(
        (volumes[mvmt].LT - intx[mvmt].LT) * leftTurnFactor
      );

      // Determine Critical Volumes for each direction
      // MAX(IFERROR(Z5SBL/LTAF/Z5SBLlanes,0),IFERROR(Z5SBT/Z5SBTlanes,0),IFERROR(Z5SBR/RTAF/Z5SBRlanes,0))
      // console.log(
      //   "MAX: ",
      //   errDiv(intx[mvmt].LT, this.LTAF, this.LaneConfig[mvmt].LTLanes),
      //   errDiv(intx[mvmt].T, this.LaneConfig[mvmt].TLanes),
      //   errDiv(intx[mvmt].RT, this.RTAF, this.LaneConfig[mvmt].RTLanes) -
      //     errDiv(intx[adjmvmt].LT, this.LTAF, this.LaneConfig[adjmvmt].LTLanes)
      // );
      intx[mvmt].criticalVol = Math.round(
        Math.max(
          errDiv(intx[mvmt].LT, this.LTAF, this.LaneConfig[mvmt].LTLanes),
          errDiv(intx[mvmt].T, this.LaneConfig[mvmt].TLanes),
          errDiv(intx[mvmt].RT, this.RTAF, this.LaneConfig[mvmt].RTLanes) -
            errDiv(
              intx[adjmvmt].LT,
              this.LTAF,
              this.LaneConfig[adjmvmt].LTLanes
            )
        )
      );
    }

    if (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH) {
      intx.Z5CLV = intx.Major1.criticalVol + intx.Minor1.criticalVol;
      intx.Z6CLV = intx.Major2.criticalVol + intx.Minor2.criticalVol;
    } else {
      intx.Z5CLV = intx.Major2.criticalVol + intx.Minor2.criticalVol;
      intx.Z6CLV = intx.Major1.criticalVol + intx.Minor1.criticalVol;
    }
    intx.Z5VC = intx.Z5CLV / this.CLV_Limit_2;
    intx.Z6VC = intx.Z6CLV / this.CLV_Limit_2;

    // if (DEBUG) {
    //   console.log(
    //     this.majorStDirection === ORIENTATIONS.EAST_WEST
    //       ? "Major 1 L/T/R (EB): " +
    //           intx.Major1.LT +
    //           ", " +
    //           intx.Major1.T +
    //           ", " +
    //           intx.Major1.RT
    //       : "Major 1 L/T/R (SB): " +
    //           intx.Major1.LT +
    //           ", " +
    //           intx.Major1.T +
    //           ", " +
    //           intx.Major1.RT
    //   );
    // }
    // if (DEBUG) {
    //   console.log(
    //     this.majorStDirection === ORIENTATIONS.EAST_WEST
    //       ? "Major 2 L/T/R (WB): " +
    //           intx.Major2.LT +
    //           ", " +
    //           intx.Major2.T +
    //           ", " +
    //           intx.Major2.RT
    //       : "Major 2 L/T/R (NB): " +
    //           intx.Major2.LT +
    //           ", " +
    //           intx.Major2.T +
    //           ", " +
    //           intx.Major2.RT
    //   );
    // }
    // if (DEBUG) {
    //   console.log(
    //     this.majorStDirection === ORIENTATIONS.EAST_WEST
    //       ? "Minor 1 L/T/R (NB): " +
    //           intx.Minor1.LT +
    //           ", " +
    //           intx.Minor1.T +
    //           ", " +
    //           intx.Minor1.RT
    //       : "Minor 1 L/T/R (EB): " +
    //           intx.Minor1.LT +
    //           ", " +
    //           intx.Minor1.T +
    //           ", " +
    //           intx.Minor1.RT
    //   );
    // }
    // if (DEBUG) {
    //   console.log(
    //     this.majorStDirection === ORIENTATIONS.EAST_WEST
    //       ? "Minor 2 L/T/R (SB): " +
    //           intx.Minor2.LT +
    //           ", " +
    //           intx.Minor2.T +
    //           ", " +
    //           intx.Minor2.RT
    //       : "Minor 2 L/T/R (WB): " +
    //           intx.Minor2.LT +
    //           ", " +
    //           intx.Minor2.T +
    //           ", " +
    //           intx.Minor2.RT
    //   );
    // }
    // if (DEBUG) {
    //   console.log(
    //     this.majorStDirection === ORIENTATIONS.EAST_WEST
    //       ? "Major 1 CritVol (EB): " + intx.Major1.criticalVol
    //       : "Major 1 CritVol (SB): " + intx.Major1.criticalVol
    //   );
    // }
    // if (DEBUG) {
    //   console.log(
    //     this.majorStDirection === ORIENTATIONS.EAST_WEST
    //       ? "(Major 2 CritVol WB): " + intx.Major2.criticalVol
    //       : "(Major 2 CritVol NB): " + intx.Major2.criticalVol
    //   );
    // }
    // if (DEBUG) {
    //   console.log(
    //     this.majorStDirection === ORIENTATIONS.EAST_WEST
    //       ? "Minor 1 CritVol (NB): " + intx.Minor1.criticalVol
    //       : "Minor 1 CritVol (EB): " + intx.Minor1.criticalVol
    //   );
    // }
    // if (DEBUG) {
    //   console.log(
    //     this.majorStDirection === ORIENTATIONS.EAST_WEST
    //       ? "Minor 2 CritVol (SB): " + intx.Minor2.criticalVol
    //       : "Minor 2 CritVol (WB): " + intx.Minor2.criticalVol
    //   );
    // }
    // if (DEBUG) {
    //   console.log("Z5: " + intx.Z5CLV + ", " + intx.Z5VC.toFixed(2));
    // }
    // if (DEBUG) {
    //   console.log("Z6: " + intx.Z6CLV + ", " + intx.Z6VC.toFixed(2));
    // }

    // Assign results for each zone
    this._resultsByZone = {
      Z5: {
        VC: intx.Z5VC,
        CLV: intx.Z5CLV,
      },
      Z6: {
        VC: intx.Z6VC,
        CLV: intx.Z6CLV,
      },
    };
  }

  getAccommodation() {
    return "+";
  }
}
